(() => {
  const readyClass = 'video-ready';
  const MAX_RETRIES = 3;
  const RETRY_COOLDOWN = 5000;
  const MOBILE_FOCUS_SCALE_DELTA = 0.1;
  const DESKTOP_FOCUS_SCALE_DELTA = 0.04;
  const DESKTOP_MQ = '(min-width: 990px)';
  const TAP_MOVE_THRESHOLD = 10;
  const SWIPE_DISTANCE_THRESHOLD = 36;
  const SWIPE_VELOCITY_THRESHOLD = 0.25;
  const SLIDE_SNAP_DURATION = 240;

  // --- Centralized visibility handler (one listener, not per-block) ---
  const visibilityCallbacks = [];
  let visibilityListenerAdded = false;

  const onVisibilityChange = (cb) => {
    visibilityCallbacks.push(cb);
    if (!visibilityListenerAdded) {
      visibilityListenerAdded = true;
      document.addEventListener('visibilitychange', () => {
        const hidden = document.hidden;
        for (let i = 0; i < visibilityCallbacks.length; i++) {
          visibilityCallbacks[i](hidden);
        }
      });
    }
  };

  // --- Lightweight video recovery ---
  const attachRecovery = (video, shouldBePlayingFn) => {
    let retries = 0;
    let cooldownTimer = null;
    let recoveryTimer = null;

    const resetRetries = () => {
      retries = 0;
      clearTimeout(cooldownTimer);
      cooldownTimer = null;
    };

    const attemptRecovery = () => {
      clearTimeout(recoveryTimer);
      if (document.hidden) return;
      if (!shouldBePlayingFn()) return;
      if (retries >= MAX_RETRIES) {
        if (!cooldownTimer) {
          cooldownTimer = setTimeout(resetRetries, RETRY_COOLDOWN);
        }
        return;
      }

      recoveryTimer = setTimeout(() => {
        if (document.hidden || !shouldBePlayingFn()) return;
        retries++;

        if (video.readyState >= 3) {
          // Enough data buffered — just play
          video.play().catch(() => {});
        } else if (video.readyState >= 1 && video.src) {
          // Has metadata but not enough data — reload current src
          video.load();
          video.play().catch(() => {});
        }
        // readyState 0 with no src: nothing to recover
      }, 2000);
    };

    video.addEventListener('waiting', attemptRecovery);
    video.addEventListener('stalled', attemptRecovery);
    video.addEventListener('playing', () => {
      clearTimeout(recoveryTimer);
      resetRetries();
    });

    // Safari silent pause recovery
    video.addEventListener('pause', () => {
      if (document.hidden) return;
      if (!shouldBePlayingFn()) return;
      // Small delay to distinguish intentional pause from Safari eviction
      setTimeout(() => {
        if (!video.paused) return; // Already resumed
        if (document.hidden || !shouldBePlayingFn()) return;
        retries++;
        if (retries <= MAX_RETRIES) {
          video.play().catch(() => {});
        }
      }, 300);
    });
  };

  // --- Preview video initialization ---
  const initPreviewVideo = (card) => {
    const src = card.dataset.videoSrc;
    const video = card.querySelector('video');
    if (!src || !video || video.dataset.loaded === 'true') return;

    video.dataset.loaded = 'true';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = src;

    let played = false;
    const markReady = () => {
      card.classList.add(readyClass);
    };

    const tryPlay = () => {
      if (played) return;
      played = true;
      const p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(markReady).catch(() => {
          if (video.readyState >= 2) markReady();
          else video.addEventListener('loadeddata', markReady, { once: true });
        });
      } else {
        markReady();
      }
    };

    video.addEventListener('loadeddata', tryPlay, { once: true });
    video.addEventListener('canplay', tryPlay, { once: true });
    video.load();
  };

  // --- Block initialization ---
  const initBlock = (block) => {
    if (block.dataset.ugcInit === 'true') return;
    block.dataset.ugcInit = 'true';

    const cards = Array.from(block.querySelectorAll('.ugc-card'));
    if (!cards.length) return;
    const row = block.querySelector('.ugc-row');
    const cardShells = cards.map((card) => card.closest('.ugc-card-shell'));

    cards.forEach((card, index) => {
      card.dataset.index = String(index);
    });

    const modal = block.querySelector('.ugc-modal');
    const slider = modal?.querySelector('.ugc-slider');
    const slideContainers = slider ? Array.from(slider.querySelectorAll('.ugc-slide')) : [];
    const closeBtn = modal?.querySelector('.ugc-close');
    const prevBtn = modal?.querySelector('.ugc-prev');
    const nextBtn = modal?.querySelector('.ugc-next');
    const soundBtn = modal?.querySelector('.ugc-sound');
    const soundIcon = soundBtn?.querySelector('.ugc-sound-icon');
    const mutedSVG = '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19"></polygon><line x1="23" y1="9" x2="17" y2="15"></line><line x1="17" y1="9" x2="23" y2="15"></line>';
    const unmutedSVG = '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19"></polygon><path d="M15 9a5 5 0 0 1 0 6"></path><path d="M19 5a9 9 0 0 1 0 14"></path>';
    const socialLinks = modal ? Array.from(modal.querySelectorAll('.ugc-social-link[data-slide-index]')) : [];

    if (!modal || !slider || !slideContainers.length || !closeBtn || !prevBtn || !nextBtn || !soundBtn) return;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const saveData = Boolean(connection && connection.saveData);
    const previewRootMargin = saveData ? '200px 0px' : '500px 0px';
    const previewLoadDelay = Math.max(0, Number(block.dataset.loadDelay || 0) || 0);

    let current = 0;
    let muted = true;
    let modalOpen = false;
    let focusFrame = null;
    let focusActivated = true;
    let rowWidth = 0;
    let maxScrollLeft = 0;
    let averageStep = 0;
    let shellWidths = [];
    let shellCenters = [];
    let rowPointerStart = null;
    let rowWasDragged = false;
    let modalPointerStart = null;
    let modalWasDragged = false;
    let modalDragFrame = null;
    let pendingModalDeltaX = 0;
    let slideTransitioning = false;
    let slideTransitionTimer = null;

    // Desktop uses a subtler zoom than mobile; recomputed on resize.
    let focusScaleDelta = window.matchMedia(DESKTOP_MQ).matches
      ? DESKTOP_FOCUS_SCALE_DELTA
      : MOBILE_FOCUS_SCALE_DELTA;

    // Track the video currently re-parented into the modal
    let activeModalVideo = null;
    let activeModalVideoCard = null;

    const muteEveryOtherUgcVideo = (activeVideo) => {
      document.querySelectorAll('[data-ugc-reels] video').forEach((video) => {
        if (video !== activeVideo) video.muted = true;
      });
    };

    const updateSoundIcon = () => {
      if (soundIcon) soundIcon.innerHTML = muted ? mutedSVG : unmutedSVG;
    };

    const enableActiveVideoSound = async () => {
      if (!activeModalVideo) return;

      muteEveryOtherUgcVideo(activeModalVideo);
      muted = false;
      activeModalVideo.muted = false;
      activeModalVideo.volume = 1;
      updateSoundIcon();

      try {
        await activeModalVideo.play();
      } catch (error) {
        console.error('UGC video could not play with sound:', error);
      }
    };

    const resetModalSound = () => {
      muted = true;
      if (activeModalVideo) activeModalVideo.muted = true;
      updateSoundIcon();
    };

    // Track which preview cards are visible
    const cardVisible = new Map();
    cards.forEach((card) => cardVisible.set(card, false));

    // Track which card index is closest to center (for z-index updates)
    let centeredIndex = -1;

    const setCardTransform = (cardShell, progress, offsetX) => {
      if (!cardShell) return;
      const scale = 1 + progress * focusScaleDelta;
      cardShell.style.transform = `translate3d(${offsetX}px, 0, 0)`;
      const inner = cardShell.querySelector('.ugc-card');
      if (inner) inner.style.transform = `scale(${scale})`;
    };

    const clearCardTransforms = () => {
      const prevCenter = centeredIndex;
      centeredIndex = -1;
      cardShells.forEach((cardShell, i) => {
        if (!cardShell) return;
        cardShell.style.transform = '';
        cardShell.style.willChange = '';
        cardShell.style.zIndex = '';
        const inner = cardShell.querySelector('.ugc-card');
        if (inner) inner.style.transform = '';
      });
    };

    const setFocusActivated = (value) => {
      focusActivated = value;
      block.dataset.ugcFocusActive = value ? 'true' : 'false';

      if (!value) {
        clearCardTransforms();
        return;
      }

      queueCenteredCardUpdate();
    };

    let rowLeftCache = 0;

    const measureCarousel = () => {
      if (!row || !cardShells.length) {
        rowWidth = 0;
        maxScrollLeft = 0;
        averageStep = 0;
        shellWidths = [];
        shellCenters = [];
        rowLeftCache = 0;
        return;
      }

      rowWidth = row.clientWidth;
      maxScrollLeft = Math.max(0, row.scrollWidth - row.clientWidth);
      rowLeftCache = row.getBoundingClientRect().left;
      shellWidths = cardShells.map((cardShell) => cardShell?.offsetWidth || 0);
      shellCenters = cardShells.map((cardShell, index) => {
        const width = shellWidths[index] || 0;
        return (cardShell?.offsetLeft || 0) + width / 2;
      });

      if (shellCenters.length <= 1) {
        averageStep = rowWidth / 3;
        return;
      }

      let totalStep = 0;
      for (let index = 1; index < shellCenters.length; index++) {
        totalStep += Math.abs(shellCenters[index] - shellCenters[index - 1]);
      }
      averageStep = totalStep / (shellCenters.length - 1);
    };

    const smoothstep = (min, max, value) => {
      if (min === max) return value >= max ? 1 : 0;
      const clamped = Math.min(1, Math.max(0, (value - min) / (max - min)));
      return clamped * clamped * (3 - 2 * clamped);
    };

    const updateCenteredCard = () => {
      focusFrame = null;
      if (!row || !cardShells.length || !focusActivated) {
        clearCardTransforms();
        return;
      }

      // Use cached rowLeft — no getBoundingClientRect in the hot path
      const rowCenter = rowLeftCache + rowWidth / 2;
      const scrollLeft = row.scrollLeft;
      const effectiveAverageStep = averageStep > 0 ? averageStep : rowWidth > 0 ? rowWidth / 3 : 200;
      const edgeRamp = Math.max(effectiveAverageStep, 1);
      const startBias = Math.max(0, 1 - scrollLeft / edgeRamp);
      const endBias = Math.max(0, 1 - (maxScrollLeft - scrollLeft) / edgeRamp);
      const biasedFocusCenter = rowCenter + (startBias - endBias) * effectiveAverageStep;
      const edgeFade = Math.min(
        1,
        Math.max(0, scrollLeft / edgeRamp),
        Math.max(0, (maxScrollLeft - scrollLeft) / edgeRamp)
      );

      // Single pass: compute progress, offsets, and find the new center card
      const count = cardShells.length;
      const progresses = new Array(count);
      let bestIndex = 0;
      let bestProgress = -1;

      for (let i = 0; i < count; i++) {
        const cardCenter = rowLeftCache + shellCenters[i] - scrollLeft;
        const normalizedDistance = Math.abs(cardCenter - biasedFocusCenter) / Math.max(effectiveAverageStep, 1);
        const p = (1 - smoothstep(0, 2, normalizedDistance)) * edgeFade;
        progresses[i] = p;
        if (p > bestProgress) {
          bestProgress = p;
          bestIndex = i;
        }
      }

      // Compute cumulative offsets (spacing compensation)
      const offsets = new Array(count);
      offsets[0] = 0;
      for (let i = 1; i < count; i++) {
        const prevExtra = (shellWidths[i - 1] || 0) * (progresses[i - 1] * focusScaleDelta);
        const currExtra = (shellWidths[i] || 0) * (progresses[i] * focusScaleDelta);
        offsets[i] = offsets[i - 1] + (prevExtra + currExtra) * 0.5;
      }

      let offsetSum = 0;
      for (let i = 0; i < count; i++) offsetSum += offsets[i];
      const avgOffset = offsetSum / count;

      // Update z-index and will-change only when the centered card changes
      if (bestIndex !== centeredIndex) {
        const prevCenter = centeredIndex;
        centeredIndex = bestIndex;

        // Clear old center neighborhood
        for (let i = Math.max(0, prevCenter - 1); i <= Math.min(count - 1, prevCenter + 1); i++) {
          if (cardShells[i]) {
            cardShells[i].style.zIndex = '';
            cardShells[i].style.willChange = '';
          }
        }

        // Promote new center neighborhood
        for (let i = Math.max(0, bestIndex - 1); i <= Math.min(count - 1, bestIndex + 1); i++) {
          if (cardShells[i]) {
            cardShells[i].style.willChange = 'transform';
            cardShells[i].style.zIndex = (i === bestIndex) ? '3' : '2';
          }
        }
      }

      // Single write pass: set transform directly on each element
      for (let i = 0; i < count; i++) {
        setCardTransform(cardShells[i], progresses[i], offsets[i] - avgOffset);
      }
    };

    const queueCenteredCardUpdate = () => {
      if (!focusActivated) return;
      if (focusFrame !== null) return;
      focusFrame = window.requestAnimationFrame(updateCenteredCard);
    };

    const handleRowScroll = () => {
      if (!row || !focusActivated) return;
      queueCenteredCardUpdate();
    };

    // --- Video re-parenting helpers ---
    const returnVideoToCard = (pause) => {
      if (!activeModalVideo || !activeModalVideoCard) return;

      const slideContainer = activeModalVideo.closest('.ugc-slide');
      if (slideContainer) slideContainer.classList.remove('is-ready');

      // Pause only when navigating away (not on close — keeps transition smooth)
      if (pause && !activeModalVideo.paused) activeModalVideo.pause();

      // Re-mute for preview playback
      activeModalVideo.muted = true;
      activeModalVideoCard.appendChild(activeModalVideo);
      activeModalVideo = null;
      activeModalVideoCard = null;
    };

    const moveVideoToModal = (index, restartAtBeginning = false) => {
      // Return previous video to its card first (pause it — we're navigating away)
      returnVideoToCard(true);

      const card = cards[index];
      const video = card?.querySelector('video');
      const slideContainer = slideContainers[index];

      if (!video || !slideContainer) return;

      activeModalVideo = video;
      activeModalVideoCard = card;

      // The initial card click keeps the preview's current playback position.
      // Subsequent modal navigation starts the destination video at frame zero.
      if (restartAtBeginning && video.readyState >= 1) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (error) {
          // Metadata can briefly become unavailable on memory-constrained Safari.
        }
      }

      // Keep the poster visible while a restarted video seeks to frame zero.
      // This prevents the previous preview frame flashing before playback begins.
      if (restartAtBeginning && video.seeking) {
        slideContainer.classList.remove('is-ready');
        video.addEventListener('seeked', () => {
          if (video.parentElement === slideContainer) {
            slideContainer.classList.add('is-ready');
          }
        }, { once: true });
      } else {
        slideContainer.classList.add('is-ready');
      }

      video.style.transition = 'none';
      slideContainer.appendChild(video);
      // Restore transition on next frame to avoid forced reflow
      requestAnimationFrame(() => { video.style.transition = ''; });

      // Apply modal playback settings and ensure playback
      video.muted = muted;
      if (video.paused) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    };

    // --- Modal open/close ---
    const modalParent = modal.parentNode;

    const openModal = (index, enableSound) => {
      current = index;
      modalOpen = true;
      muted = !enableSound;

      // Pause other preview videos, but NOT the clicked one (it will keep playing seamlessly)
      cards.forEach((card, i) => {
        if (i === index) return;
        const v = card.querySelector('video');
        if (v && !v.paused) v.pause();
      });

      document.body.appendChild(modal);
      modal.classList.add('active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      updateSlide();
      if (enableSound) enableActiveVideoSound();
    };

    const closeModal = () => {
      if (slideTransitioning) {
        clearTimeout(slideTransitionTimer);
        slideTransitioning = false;
        slider.style.transition = 'none';
        slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
        moveVideoToModal(current, true);
      }

      modal.classList.remove('active');
      modal.setAttribute('aria-hidden', 'true');
      modalOpen = false;

      // Move video back to its card without pausing — avoids visible stutter.
      // returnVideoToCard re-mutes it; the card intersection observer
      // will pause it if the card is off-screen.
      returnVideoToCard();

      document.body.style.overflow = '';
      modalParent.appendChild(modal);

      // Resume only currently visible preview videos (handles cards
      // whose videos were paused when the modal opened)
      cards.forEach((card) => {
        if (!cardVisible.get(card)) return;
        const v = card.querySelector('video');
        if (v && v.src && v.paused) {
          v.play().catch(() => {});
        }
      });
    };

    // --- Modal slide update ---
    const updateSlide = (restartAtBeginning = false) => {
      clearTimeout(slideTransitionTimer);
      slideTransitioning = false;
      slider.style.transition = 'none';
      slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;

      // Move the correct preview video into the current modal slide
      moveVideoToModal(current, restartAtBeginning);

      updateSoundIcon();
      socialLinks.forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.slideIndex === String(current));
      });
    };

    const finishSlideTransition = () => {
      if (!slideTransitioning) return;
      clearTimeout(slideTransitionTimer);
      slideTransitioning = false;
      slider.style.transition = '';

      // Keep the outgoing video in place for the entire slide animation.
      // Re-parent only after the destination slide has settled.
      moveVideoToModal(current, true);
      updateSoundIcon();
    };

    const animateToSlide = (nextIndex, duration = SLIDE_SNAP_DURATION) => {
      if (slideTransitioning || nextIndex === current) {
        slider.style.transition = `transform ${Math.min(duration, 180)}ms cubic-bezier(0.16, 1, 0.3, 1)`;
        slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
        return;
      }

      const previousIndex = current;
      current = nextIndex;

      socialLinks.forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.slideIndex === String(current));
      });

      // A wrap from the first to last slide (or vice versa) would cross every
      // intermediate slide. Switch instantly at that boundary instead.
      if (Math.abs(current - previousIndex) > 1) {
        updateSlide(true);
        return;
      }

      slideTransitioning = true;
      // Flush the final drag position before enabling the snap transition.
      // This avoids a one-frame jump when pointerup lands between animation frames.
      slider.getBoundingClientRect();
      slider.style.transition = `transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1)`;
      slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;

      slideTransitionTimer = window.setTimeout(finishSlideTransition, duration + 80);
    };

    slider.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform') finishSlideTransition();
    });

    // --- Tab visibility handling ---
    onVisibilityChange((hidden) => {
      if (hidden) {
        // Pause active playback cleanly
        if (modalOpen) {
          if (activeModalVideo && !activeModalVideo.paused) activeModalVideo.pause();
        } else {
          cards.forEach((card) => {
            const v = card.querySelector('video');
            if (v && !v.paused) v.pause();
          });
        }
      } else {
        // Resume appropriate playback
        if (modalOpen) {
          if (activeModalVideo && activeModalVideo.paused && activeModalVideo.src) {
            activeModalVideo.muted = muted;
            activeModalVideo.play().catch(() => {});
          }
        } else {
          cards.forEach((card) => {
            if (!cardVisible.get(card)) return;
            const v = card.querySelector('video');
            if (v && v.src && v.paused) {
              v.play().catch(() => {});
            }
          });
        }
      }
    });

    // --- Preview card visibility observer ---
    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const card = entry.target;
        const wasVisible = cardVisible.get(card);
        cardVisible.set(card, entry.isIntersecting);

        const video = card.querySelector('video');
        if (!video || !video.src) return;

        if (entry.isIntersecting && !wasVisible && !modalOpen) {
          if (video.paused) video.play().catch(() => {});
        } else if (!entry.isIntersecting && wasVisible) {
          if (!video.paused) video.pause();
        }
      });
    }, { threshold: 0.2 });

    // --- Preview video recovery (also covers modal playback) ---
    cards.forEach((card) => {
      const video = card.querySelector('video');
      if (video) {
        attachRecovery(video, () => {
          // Video is playing in the modal
          if (modalOpen && activeModalVideo === video) return true;
          // Video is playing as a preview
          return !modalOpen && cardVisible.get(card) === true;
        });
      }
    });

    // --- Block viewport observer (triggers preview video init) ---
    const blockObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        blockObserver.disconnect();

        // First card initializes immediately — no idle callback delay
        initPreviewVideo(cards[0]);

        // Remaining cards stagger to smooth network load
        cards.slice(1).forEach((card, index) => {
          const delay = previewLoadDelay > 0
            ? previewLoadDelay * (index + 1)
            : (index + 1) * 120;
          window.setTimeout(() => initPreviewVideo(card), delay);
        });
      });
    }, { rootMargin: previewRootMargin, threshold: 0 });

    // --- Card event listeners ---
    cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (rowWasDragged) {
          event.preventDefault();
          return;
        }
        openModal(Number(card.dataset.index || 0), true);
      });

      // Start observing card visibility
      cardObserver.observe(card);
    });

    if (row) {
      measureCarousel();
      setFocusActivated(true);
      row.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return;
        rowPointerStart = { x: event.clientX, y: event.clientY };
        rowWasDragged = false;
      }, { passive: true });
      row.addEventListener('pointermove', (event) => {
        if (!rowPointerStart || !event.isPrimary) return;
        const distanceX = Math.abs(event.clientX - rowPointerStart.x);
        const distanceY = Math.abs(event.clientY - rowPointerStart.y);
        if (distanceX > TAP_MOVE_THRESHOLD || distanceY > TAP_MOVE_THRESHOLD) {
          rowWasDragged = true;
        }
      }, { passive: true });
      const finishRowPointer = () => {
        rowPointerStart = null;
        window.setTimeout(() => {
          rowWasDragged = false;
        }, 0);
      };
      row.addEventListener('pointerup', finishRowPointer, { passive: true });
      row.addEventListener('pointercancel', finishRowPointer, { passive: true });
      row.addEventListener('scroll', handleRowScroll, { passive: true });
      window.addEventListener('resize', () => {
        focusScaleDelta = window.matchMedia(DESKTOP_MQ).matches
          ? DESKTOP_FOCUS_SCALE_DELTA
          : MOBILE_FOCUS_SCALE_DELTA;
        measureCarousel();
        centeredIndex = -1;
        setFocusActivated(true);
        queueCenteredCardUpdate();
      }, { passive: true });
      queueCenteredCardUpdate();
    }

    blockObserver.observe(block);

    // --- Modal controls ---
    closeBtn.addEventListener('click', closeModal);

    prevBtn.addEventListener('click', () => {
      animateToSlide((current - 1 + slideContainers.length) % slideContainers.length);
    });

    nextBtn.addEventListener('click', () => {
      animateToSlide((current + 1) % slideContainers.length);
    });

    soundBtn.addEventListener('click', () => {
      if (muted) {
        enableActiveVideoSound();
      } else {
        resetModalSound();
      }
    });

    slider.addEventListener('pointerdown', (event) => {
      if (slideTransitioning || !event.isPrimary || !event.target.closest('video')) return;
      try {
        slider.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture is optional on older Safari versions.
      }
      modalPointerStart = {
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
        startedOnVideo: true
      };
      modalWasDragged = false;
    }, { passive: true });

    slider.addEventListener('pointermove', (event) => {
      if (!modalPointerStart || !event.isPrimary) return;
      const deltaX = event.clientX - modalPointerStart.x;
      const deltaY = event.clientY - modalPointerStart.y;
      const distanceX = Math.abs(deltaX);
      const distanceY = Math.abs(deltaY);
      if (distanceX > TAP_MOVE_THRESHOLD || distanceY > TAP_MOVE_THRESHOLD) {
        modalWasDragged = true;
      }

      if (distanceX > distanceY && modalWasDragged) {
        event.preventDefault();
        let visualDeltaX = deltaX;
        const draggingPastStart = current === 0 && deltaX > 0;
        const draggingPastEnd = current === slideContainers.length - 1 && deltaX < 0;
        if (draggingPastStart || draggingPastEnd) visualDeltaX *= 0.22;

        pendingModalDeltaX = visualDeltaX;
        if (modalDragFrame === null) {
          modalDragFrame = requestAnimationFrame(() => {
            modalDragFrame = null;
            slider.style.transition = 'none';
            slider.style.transform = `translate3d(calc(-${current * 100}% + ${pendingModalDeltaX}px), 0, 0)`;
          });
        }
      }
    }, { passive: false });

    slider.addEventListener('pointerup', (event) => {
      if (!modalPointerStart || !event.isPrimary) return;
      const deltaX = event.clientX - modalPointerStart.x;
      const deltaY = event.clientY - modalPointerStart.y;
      const elapsed = Math.max(1, performance.now() - modalPointerStart.time);
      const velocityX = Math.abs(deltaX) / elapsed;
      const isHorizontalSwipe = modalWasDragged
        && Math.abs(deltaX) > Math.abs(deltaY)
        && (
          Math.abs(deltaX) >= SWIPE_DISTANCE_THRESHOLD
          || velocityX >= SWIPE_VELOCITY_THRESHOLD
        );
      const shouldEnableSound = !modalWasDragged && modalPointerStart.startedOnVideo;

      if (modalDragFrame !== null) {
        cancelAnimationFrame(modalDragFrame);
        modalDragFrame = null;
        slider.style.transition = 'none';
        slider.style.transform = `translate3d(calc(-${current * 100}% + ${pendingModalDeltaX}px), 0, 0)`;
      }

      modalPointerStart = null;
      modalWasDragged = false;

      if (isHorizontalSwipe) {
        const nextIndex = deltaX < 0
          ? Math.min(current + 1, slideContainers.length - 1)
          : Math.max(current - 1, 0);
        const snapDuration = Math.max(160, SLIDE_SNAP_DURATION - Math.min(70, velocityX * 90));
        animateToSlide(nextIndex, Math.round(snapDuration));
      } else {
        slider.style.transition = 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1)';
        slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
        if (shouldEnableSound) enableActiveVideoSound();
      }
    }, { passive: true });

    slider.addEventListener('pointercancel', () => {
      if (modalDragFrame !== null) {
        cancelAnimationFrame(modalDragFrame);
        modalDragFrame = null;
      }
      modalPointerStart = null;
      modalWasDragged = false;
      slider.style.transition = 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1)';
      slider.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
    }, { passive: true });

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
  };

  // --- Entry point ---
  const initAll = () => {
    document.querySelectorAll('[data-ugc-reels]').forEach(initBlock);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
